import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, Users, Loader2, CreditCard, Gift, CheckCircle2, X } from "lucide-react";
import { format } from "date-fns";
import { createBooking, validateVoucher, fetchBlockedDates } from "@/lib/api";
import { SCHEDULE, IMAGES, SATURDAY_NOTE, BLOCKED_DATES as STATIC_BLOCKED } from "@/data/content";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import DirectBookingCard from "@/components/DirectBookingCard";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

// Tier packages — must mirror backend TIER_PRICES_AUD.
const BASE_RATE = 35;
const TIER_PRICES = {
  1: 35, 2: 65, 3: 90, 4: 115, 5: 140,
  6: 165, 7: 185, 8: 205, 9: 225, 10: 250,
};
const EXTRA_GUEST = 25;
const MAX = 30;

function calcTourTotal(guests) {
  const g = Number(guests) || 0;
  if (g < 1) return 0;
  if (g <= 10) return TIER_PRICES[g];
  return TIER_PRICES[10] + (g - 10) * EXTRA_GUEST;
}

const SLOTS = [
  { day: "Friday", label: "Friday · 11:00 AM – 12:45 PM" },
  { day: "Sunday", label: "Sunday · 1:00 PM – 3:00 PM" },
];

const isLastSunday = (d) => {
  const next = new Date(d);
  next.setDate(d.getDate() + 7);
  return d.getDay() === 0 && next.getMonth() !== d.getMonth();
};

export default function IndividualTours() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", notes: "",
    guests: 2, time_slot: "", date: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null); // { code, seats_remaining }
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [blockedDates, setBlockedDates] = useState(STATIC_BLOCKED);

  useEffect(() => {
    fetchBlockedDates()
      .then((docs) => {
        if (Array.isArray(docs) && docs.length) {
          setBlockedDates(docs.map((d) => d.date));
        }
      })
      .catch((err) => console.error("Failed to fetch blocked dates:", err));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const guestsN = Number(form.guests || 0);
  const seatsCovered = voucher ? Math.min(voucher.seats_remaining, guestsN) : 0;
  const tourTotal = calcTourTotal(guestsN);
  const voucherCredit = Math.min(seatsCovered * BASE_RATE, tourTotal);
  const total = Math.max(0, tourTotal - voucherCredit);
  const perPerson = guestsN > 0 ? tourTotal / guestsN : 0;

  const applyVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code) return;
    setVoucherChecking(true);
    try {
      const res = await validateVoucher(code);
      if (!res.valid) {
        toast.error(res.reason || "Voucher not valid");
        setVoucher(null);
        return;
      }
      setVoucher(res);
      toast.success(`Voucher applied — covers up to ${res.seats_remaining} seat${res.seats_remaining > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voucher not found");
      setVoucher(null);
    } finally {
      setVoucherChecking(false);
    }
  };
  const clearVoucher = () => { setVoucher(null); setVoucherInput(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_slot) {
      toast.error("Pick a date and time slot to continue");
      return;
    }
    if (!form.full_name || !form.email) {
      toast.error("We need your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        tour_date: format(form.date, "yyyy-MM-dd"),
        time_slot: form.time_slot,
        guests: Number(form.guests),
        notes: form.notes || null,
        origin_url: window.location.origin,
        voucher_code: voucher ? voucher.code : null,
      };
      const res = await createBooking(payload);
      if (res.checkout_url) {
        // Stripe live — redirect to hosted checkout
        window.location.href = res.checkout_url;
        return;
      }
      // Voucher fully covered OR no Stripe — show confirmation
      setConfirmed(res.booking || res);
      toast.success(payload.voucher_code ? "Voucher redeemed — booking confirmed!" : "Booking confirmed!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or contact Martin directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="booking-confirmed">
        <div className="serif text-5xl text-[#6E2638]">You're on the walk.</div>
        <OrnamentalDivider label="Booking confirmed" />
        <p className="text-[#1A1817]/80 mt-2">
          Thanks {confirmed.full_name} — we've recorded {confirmed.guests} guest(s) for{" "}
          <strong>{confirmed.time_slot}</strong> on <strong>{confirmed.tour_date}</strong>.
        </p>
        <div className="card-warm rounded-2xl p-6 mt-8 text-left">
          <div className="text-sm text-[#1A1817]/70">Reference</div>
          <div className="serif text-xl text-[#6E2638] break-all">{confirmed.id}</div>
          <div className="text-sm mt-4 text-[#1A1817]/70">Total</div>
          <div className="serif text-3xl text-[#6E2638]">AUD ${confirmed.total_price_aud.toFixed(2)}</div>
        </div>
        <div className="mt-6 text-[#1A1817]/70 text-sm">
          Payment was completed securely via Stripe. Meeting point: Federation Square — outside the big screen. A confirmation email is on its way.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="individual-page">
      {/* HERO */}
      <section className="relative h-[46vh] min-h-[360px] overflow-hidden">
        <img src={IMAGES.tram} alt="Melbourne tram" className="absolute inset-0 w-full h-full object-cover sepia-soft" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">Individual Tours</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">Book your seat on the walk.</h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            $35 AUD per adult, with better prices for groups: <b>3 for $90</b>, <b>5 for $140</b>, all the way up to <b>10 for $250</b>. Under 12s walk free. Minimum of 3 guests required for the tour to run — Martin will confirm.
          </p>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-5 gap-10">
        {/* BOOKING FORM */}
        <form onSubmit={submit} className="lg:col-span-3 card-warm rounded-2xl p-8" data-testid="booking-form">
          <div className="stamp">Reserve your spot online</div>
          <h2 className="serif text-3xl text-[#6E2638] mt-4">Your details</h2>
          <OrnamentalDivider />

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="input-name" value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={form.email}
                onChange={(e) => update("email", e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" data-testid="input-phone" value={form.phone}
                onChange={(e) => update("phone", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="guests">Guests</Label>
              <div className="flex items-center gap-2">
                <Users size={18} className="text-[#6E2638]" />
                <Input id="guests" type="number" min={1} max={MAX} data-testid="input-guests"
                  value={form.guests} onChange={(e) => update("guests", e.target.value)} />
              </div>
              {(() => {
                if (guestsN < 1 || guestsN >= MAX) return null;
                const nextGuests = guestsN + 1;
                const delta = calcTourTotal(nextGuests) - calcTourTotal(guestsN);
                const saving = BASE_RATE - delta;
                if (saving <= 0) return null;
                return (
                  <button
                    type="button"
                    onClick={() => update("guests", nextGuests)}
                    className="mt-2 w-full rounded-lg border border-dashed border-[#B08B2E] bg-[#FDF7E7] px-3 py-2 text-left text-xs text-[#8A6A16] hover:bg-[#F9EFC9] transition-colors flex items-center justify-between gap-2"
                    data-testid="next-tier-nudge"
                  >
                    <span>
                      <b className="text-[#6E2638]">Add 1 more</b> for just <b>${delta}</b> more —
                      save <b>${saving}</b> vs the solo rate!
                    </span>
                    <span className="serif text-[#6E2638] shrink-0">→ {nextGuests}</span>
                  </button>
                );
              })()}
            </div>

            <div>
              <Label>Tour date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="date-trigger"
                    className="w-full justify-start font-normal border-[#E5D9C5]">
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#6E2638]" />
                    {form.date ? format(form.date, "EEEE, dd MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.date}
                    onSelect={(d) => {
                      update("date", d);
                      const slot = d && SLOTS.find((s) => s.day === format(d, "EEEE"));
                      if (slot) update("time_slot", slot.label);
                    }}
                    disabled={(d) => {
                      const day = d.getDay();
                      const iso = format(d, "yyyy-MM-dd");
                      // Fridays + last Sunday of each month only, no past dates, skip blocked dates
                      return d < new Date(new Date().setHours(0,0,0,0))
                        || !(day === 5 || isLastSunday(d))
                        || blockedDates.includes(iso);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-[#1A1817]/60 mt-1.5 italic" data-testid="date-help">
                Fridays &amp; the last Sunday of each month only · greyed-out dates are <b>fully booked</b>
              </p>
            </div>

            <div>
              <Label>Time slot</Label>
              <Select value={form.time_slot} onValueChange={(v) => update("time_slot", v)}>
                <SelectTrigger data-testid="slot-trigger" className="border-[#E5D9C5]">
                  <SelectValue placeholder="Choose a slot" />
                </SelectTrigger>
                <SelectContent>
                  {SLOTS.filter((s) => !form.date || s.day === format(form.date, "EEEE")).map((s) => (
                    <SelectItem key={s.label} value={s.label} data-testid={`slot-${s.day.toLowerCase()}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="notes">Special requests (optional)</Label>
            <Textarea id="notes" data-testid="input-notes" rows={3}
              value={form.notes} onChange={(e) => update("notes", e.target.value)} />
          </div>

          <div className="mt-6 rounded-2xl border border-dashed border-[#D4AF37] bg-[#FDFBF7] p-4" data-testid="voucher-block">
            <div className="flex items-center gap-2 text-[#6E2638]">
              <Gift size={18} />
              <span className="text-sm font-semibold uppercase tracking-[0.18em]">Have a gift voucher?</span>
            </div>
            {!voucher ? (
              <div className="mt-3 flex gap-2">
                <Input
                  data-testid="input-voucher-code"
                  placeholder="Enter voucher code (e.g. ABCD-EFGH-JKLM)"
                  value={voucherInput}
                  onChange={(e) => setVoucherInput(e.target.value)}
                  className="uppercase tracking-wider"
                />
                <Button type="button" onClick={applyVoucher} disabled={voucherChecking || !voucherInput.trim()}
                        variant="outline" className="border-[#6E2638] text-[#6E2638] shrink-0"
                        data-testid="apply-voucher">
                  {voucherChecking ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                </Button>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between bg-[#F5EFEB] rounded-lg p-3" data-testid="voucher-applied">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-[#1E4637]" />
                  <div>
                    <div className="serif text-[#6E2638] font-mono text-sm">{voucher.code}</div>
                    <div className="text-xs text-[#1A1817]/70">
                      Covers {seatsCovered} of {form.guests} seat{form.guests > 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={clearVoucher} className="text-[#6E2638] hover:opacity-70" data-testid="clear-voucher">
                  <X size={18} />
                </button>
              </div>
            )}
            <p className="text-xs text-[#1A1817]/60 mt-2">
              Don't have one? <Link to="/gift-vouchers" className="text-[#6E2638] underline">Buy a gift voucher</Link>.
            </p>
          </div>

          <div className="mt-6 rounded-2xl bg-[#F5EFEB] border border-[#E5D9C5] p-5 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">You'll pay</div>
              <div className="serif text-3xl text-[#6E2638]" data-testid="total-price">
                AUD ${total.toFixed(2)}
              </div>
              <div className="text-xs text-[#1A1817]/70 mt-1">
                {guestsN > 0 && (
                  <>
                    {guestsN} guest{guestsN !== 1 ? "s" : ""} package · ${tourTotal.toFixed(0)}
                    {guestsN >= 2 && <> · ~${perPerson.toFixed(0)} per person</>}
                  </>
                )}
                {seatsCovered > 0 && <> · <span className="text-[#1E4637]">−${voucherCredit.toFixed(0)} voucher credit</span></>}
                {total > 0 && <> · secured via Stripe</>}
              </div>
            </div>
            <CreditCard size={32} className="text-[#6E2638] opacity-70" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full justify-center" data-testid="submit-booking">
            {submitting ? (
              <><Loader2 className="animate-spin" size={18} /> {total > 0 ? "Redirecting to secure checkout…" : "Confirming your booking…"}</>
            ) : total > 0 ? (
              <>Confirm booking &amp; pay <CreditCard size={16} /></>
            ) : (
              <>Confirm booking (voucher covers all seats) <CheckCircle2 size={16} /></>
            )}
          </button>

          <p className="text-xs text-[#1A1817]/60 mt-4 text-center italic">
            {total > 0
              ? "You'll be taken to Stripe's secure checkout page to complete your payment."
              : "Your voucher covers all seats — no payment required."}
          </p>
        </form>

        {/* SIDEBAR */}
        <aside className="lg:col-span-2 space-y-6">
          <DirectBookingCard />

          {/* GROUP PACKAGE PRICING */}
          <div className="card-warm rounded-2xl p-8" data-testid="group-packages">
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Group packages</div>
            <h3 className="serif text-2xl text-[#6E2638] mt-2">Better prices, more people</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>1 guest</span><span className="serif text-[#6E2638]">$35</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>2 guests</span><span className="serif text-[#6E2638]">$65</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>3 guests</span><span className="serif text-[#6E2638]">$90</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>4 guests</span><span className="serif text-[#6E2638]">$115</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>5 guests</span><span className="serif text-[#6E2638]">$140</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2 text-[#1A1817]/70"><span>6-9 guests</span><span className="serif">$165 – $225</span></li>
              <li className="flex justify-between border-b border-[#E5D9C5] pb-2"><span>10 guests</span><span className="serif text-[#6E2638]">$250</span></li>
              <li className="flex justify-between text-[#1A1817]/70"><span>11+ guests</span><span className="serif">$250 + $25 each extra</span></li>
            </ul>
            <p className="text-xs text-[#1A1817]/60 mt-4 italic">
              Under 12s walk free with a paying adult · minimum 3 guests for the tour to run (Martin will confirm).
            </p>
          </div>

          <div className="card-warm rounded-2xl p-8">
            <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">Scheduled walks</div>
            <ul className="mt-3 space-y-3">
              {SCHEDULE.map((s) => (
                <li key={s.day} className="flex justify-between border-b border-[#E5D9C5] pb-2 text-sm">
                  <span className="serif text-[#6E2638] text-lg">{s.day}</span>
                  <span className="text-[#1A1817]/80">{s.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <Link to="/contact" className="btn-outline-gold w-full justify-center" data-testid="ind-more-questions">
            More questions? Contact page
          </Link>
        </aside>
      </section>
    </div>
  );
}
